/* eslint-disable react-hooks/rules-of-hooks */
import type { Branding } from "../../shared/types/commonTypes";

import { FC, useEffect, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NotificationManager } from "../../services/api/NotificationManager";
import { showFlashMessageWithTimeout } from "../../services/actions/flashMessage-actions";
import { FormType } from "../../components/types";
import {
  GetNotificationSettingsResponse,
  UpdateNotificationSettingsRequest,
} from "../../shared/payloads/notificationSettingsPayloads";
import {
  transformFormsToUrlFilterDto,
  transformUrlFilterToForms,
} from "../../generic-utils";
import DOMPurify from "dompurify";
import { Language } from "../../shared/types/websiteTypes";
import { getOrdersAndRegistrationsInitialText } from "../../utils/web-utils";
import { NotificationDefaultSettings } from "../../types/notification-default-settings";
import { BorderType } from "../../components/NotificationEditComponents/BorderSelect";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import Tabs from "../../components/Tabs";
import { NotificationDesignTab } from "../../components/notification/tabs/NotificationDesignTab";
import { NotificationModalBase } from "../../components/notification/NotificationModalBase";
import { NotificationPairingTab } from "../../components/notification/tabs/NotificationPairingTab";
import { addEmptyPairingForm } from "../../utils/forms/addEmptyPairingForm";
import { handlePairingFormControlChange } from "../../utils/forms/handlePairingFormControlChange";
import { onAddPairingFormExcludeChip } from "../../utils/forms/onAddPairingFormExcludeChip";
import { onAddPairingFormIncludeChip } from "../../utils/forms/onAddPairingFormIncludeChip";
import { onDeletePairingForm } from "../../utils/forms/onDeletePairingForm";
import { NotificationSettingsTab } from "../../components/notification/tabs/NotificationSettingsTab";
import { NotificationRulesTab } from "../../components/notification/tabs/NotificationRulesTab";
import { getCustomWebLogo } from "../../services/selectors/web-selectors";

interface OrdersAndRegistrationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
  createdNotificationId: string;
  forOrders: boolean;
  language: Language;
  data?: GetNotificationSettingsResponse;
  branding: Branding;
}

type TabType = "rules" | "pairing" | "design" | "settings";

const notificationStyleId = v4();

const getMax = (data: GetNotificationSettingsResponse): number => {
  if (data.type === "lastOrders") {
    if (data.maxLastOrders) return data.maxLastOrders;
  }
  if (data.type === "lastRegistrations") {
    if (data.maxLastRegistrations) return data.maxLastRegistrations;
  }
  return 0;
};

const getMin = (data: GetNotificationSettingsResponse): number => {
  if (data.type === "lastOrders") {
    if (data.minOrders) return data.minOrders;
  }
  if (data.type === "lastRegistrations") {
    if (data.minRegistrations) return data.minRegistrations;
  }
  return 0;
};

const OrdersAndRegistrationsModal: FC<OrdersAndRegistrationsModalProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  createdNotificationId,
  forOrders,
  language,
  data,
  branding,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<TabType>("rules");
  const [text, setText] = useState<string>(
    data?.text
      ? data.text
      : t(getOrdersAndRegistrationsInitialText(forOrders, language)) ?? "",
  );
  const [max, setMax] = useState<number>(data ? getMax(data) : 10);
  const [min, setMin] = useState<number>(data ? getMin(data) : 5);
  const [days, setDays] = useState<number>(
    data?.maxLastDays !== undefined ? data?.maxLastDays : 5,
  );
  const [fallbackToGenericCustomer, setFallbackToGenericCustomer] =
    useState<boolean>(
      data?.fallbackToGenericCustomer !== undefined
        ? data?.fallbackToGenericCustomer
        : true,
    );
  const [withRepeat, setWithRepeat] = useState<boolean>(
    data?.repeatNotification !== undefined ? data?.repeatNotification : true,
  );
  const [urlFilterForms, setUrlFilterForms] = useState<FormType[]>(
    data?.urlFilter
      ? transformUrlFilterToForms(data.urlFilter)
      : [{ control: "contains", include: ["/"], excludes: [] }],
  );

  const [notificationHtml, setNotificationHtml] = useState<string>("");
  const [showCustomerOwnOrders, setShowCustomerOwnOrders] = useState<boolean>(
    data?.showCustomerOwnOrders !== undefined
      ? data.showCustomerOwnOrders
      : true,
  );
  const [showCustomerOwnRegistrations, setShowCustomerOwnRegistrations] =
    useState<boolean>(
      data?.showCustomerOwnRegistrations !== undefined
        ? data?.showCustomerOwnRegistrations
        : true,
    );

  const [backgroundColor, setBackgroundColor] = useState<string | null>(
    data?.notificationVisualSettings?.backgroundColor || null,
  );
  const [textColor, setTextColor] = useState<string | null>(
    data?.notificationVisualSettings?.textColor || null,
  );
  const [iconBackgroundColor, setIconBackgroundColor] = useState<string | null>(
    data?.notificationVisualSettings?.iconBackgroundColor || null,
  );
  const [iconColor, setIconColor] = useState<string | null>(
    data?.notificationVisualSettings?.iconColor || null,
  );
  const [borderRadius, setBorderRadius] = useState<BorderType | null>(
    data?.notificationVisualSettings?.roundness || null,
  );
  const [icon, setIcon] = useState<string | null>(
    data?.notificationVisualSettings?.icon || null,
  );

  const [backgroundOpacity, setBackgroundOpracity] = useState<number | null>(
    null,
  );
  const [active, setActive] = useState<"classic" | "small" | "compact">(
    "classic",
  );
  const [showIcon, setShowIcon] = useState<boolean>(true);
  const [iconBackgroundRadius, setIconBackgroundRadius] = useState<
    number | null
  >(null);

  const [detectedChange, setDetectedChange] = useState<boolean>(false);

  const customWebBranding = useSelector(getCustomWebLogo);

  useEffect(() => {
    const defaultBackgroundColor =
      data?.notificationVisualSettings?.backgroundColor ??
      branding?.backgroundColor ??
      NotificationDefaultSettings.BackgroundColor;

    const defaultMax = data ? getMax(data) : 10;
    const defaultMin = data ? getMin(data) : 5;
    const defaultDays = data?.maxLastDays !== undefined ? data?.maxLastDays : 5;
    const fallbackGenericCustomerDefault =
      data?.fallbackToGenericCustomer !== undefined
        ? data?.fallbackToGenericCustomer
        : true;
    const showCustomerOwnRegistrationsDefault =
      data?.showCustomerOwnRegistrations !== undefined
        ? data?.showCustomerOwnRegistrations
        : true;
    const showCustomerOwnOrdersDefault =
      data?.showCustomerOwnOrders !== undefined
        ? data.showCustomerOwnOrders
        : true;
    const withRepeatDefault =
      data?.repeatNotification !== undefined ? data?.repeatNotification : true;

    const defaultText = data?.text
      ? data.text
      : t(getOrdersAndRegistrationsInitialText(forOrders, language)) ?? "";

    const defaultForms = data?.urlFilter
      ? transformUrlFilterToForms(data.urlFilter)
      : [{ control: "contains", include: ["/"], excludes: [] }];

    const defaultTextColor =
      data?.notificationVisualSettings?.textColor ??
      branding?.textColor ??
      NotificationDefaultSettings.TextColor;

    const activeDefault =
      data?.notificationVisualSettings?.activeSize ?? branding?.activeSize;

    const iconColorDefault =
      data?.notificationVisualSettings?.iconColor ??
      branding?.iconColor ??
      NotificationDefaultSettings.IconColor;

    const showIconDefault =
      data?.notificationVisualSettings?.isShowingIcon !== null &&
      typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true;

    const defaultIconBackgroundColor =
      data?.notificationVisualSettings?.iconBackgroundColor ??
      branding?.iconBackgroundColor ??
      iconBackgroundColor ??
      NotificationDefaultSettings.IconBackgroundColor;

    if (
      borderRadius !== null &&
      (data?.notificationVisualSettings?.roundness !== null &&
      data?.notificationVisualSettings?.roundness !== undefined
        ? borderRadius !== data?.notificationVisualSettings?.roundness
        : borderRadius !== branding?.borderRadius)
    ) {
      setDetectedChange(true);
    } else if (
      backgroundOpacity !== null &&
      (data?.notificationVisualSettings?.backgroundOpacity !== null &&
      data?.notificationVisualSettings?.backgroundOpacity !== undefined
        ? backgroundOpacity !==
          data?.notificationVisualSettings?.backgroundOpacity
        : backgroundOpacity !== branding?.backgroundOpacity)
    ) {
      setDetectedChange(true);
    } else if (backgroundColor && backgroundColor !== defaultBackgroundColor) {
      setDetectedChange(true);
    } else if (textColor && textColor !== defaultTextColor) {
      setDetectedChange(true);
    } else if (activeDefault && activeDefault !== active) {
      setDetectedChange(true);
    } else if (showIcon !== showIconDefault) {
      setDetectedChange(true);
    } else if (
      iconBackgroundColor &&
      iconBackgroundColor !== defaultIconBackgroundColor
    ) {
      setDetectedChange(true);
    } else if (
      iconBackgroundRadius &&
      (data?.notificationVisualSettings?.iconBackgroundRadius !== null &&
      data?.notificationVisualSettings?.iconBackgroundRadius !== undefined
        ? iconBackgroundRadius !==
          data?.notificationVisualSettings?.iconBackgroundRadius
        : iconBackgroundRadius !== branding?.iconBackgroundRadius)
    ) {
      setDetectedChange(true);
    } else if (iconColor && iconColor !== iconColorDefault) {
      setDetectedChange(true);
    } else if (
      icon &&
      (data?.notificationVisualSettings?.icon
        ? icon !== data?.notificationVisualSettings?.icon
        : icon !== NotificationDefaultSettings.OrdersIcon)
    ) {
      setDetectedChange(true);
    } else if (text !== defaultText) {
      setDetectedChange(true);
    } else if (
      JSON.stringify(defaultForms) !== JSON.stringify(urlFilterForms)
    ) {
      setDetectedChange(true);
    } else if (max !== defaultMax) {
      setDetectedChange(true);
    } else if (min !== defaultMin) {
      setDetectedChange(true);
    } else if (days !== defaultDays) {
      setDetectedChange(true);
    } else if (fallbackToGenericCustomer !== fallbackGenericCustomerDefault) {
      setDetectedChange(true);
    } else if (
      showCustomerOwnRegistrations !== showCustomerOwnRegistrationsDefault
    ) {
      setDetectedChange(true);
    } else if (showCustomerOwnOrders !== showCustomerOwnOrdersDefault) {
      setDetectedChange(true);
    } else if (withRepeat !== withRepeatDefault) {
      setDetectedChange(true);
    } else {
      setDetectedChange(false);
    }
  }, [
    borderRadius,
    withRepeat,
    fallbackToGenericCustomer,
    showCustomerOwnRegistrations,
    showCustomerOwnOrders,
    days,
    max,
    min,
    urlFilterForms,
    active,
    text,
    icon,
    textColor,
    iconColor,
    backgroundColor,
    backgroundOpacity,
    showIcon,
    iconBackgroundColor,
    iconBackgroundRadius,
    data?.text,
    data?.notificationVisualSettings?.icon,
    data?.notificationVisualSettings?.iconColor,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.textColor,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.roundness,
    branding?.brandingIsShowingIcon,
    branding?.borderRadius,
    branding?.backgroundOpacity,
    branding?.backgroundColor,
    branding?.textColor,
    branding?.iconColor,
    branding?.activeSize,
    branding?.iconBackgroundColor,
    branding?.iconBackgroundRadius,
  ]);

  useEffect(() => {
    setBackgroundColor(
      data?.notificationVisualSettings?.backgroundColor ??
        branding?.backgroundColor ??
        null,
    );
    setTextColor(
      data?.notificationVisualSettings?.textColor ??
        branding?.textColor ??
        null,
    );
    setIconBackgroundColor(
      data?.notificationVisualSettings?.iconBackgroundColor ??
        branding?.iconBackgroundColor ??
        null,
    );
    setBorderRadius(
      data?.notificationVisualSettings?.roundness ??
        branding?.borderRadius ??
        null,
    );
    setBackgroundOpracity(
      data?.notificationVisualSettings?.backgroundOpacity ??
        branding?.backgroundOpacity ??
        null,
    );
    setActive(
      data?.notificationVisualSettings?.activeSize ||
        branding?.activeSize ||
        "classic",
    );
    setShowIcon(
      typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true,
    );
    setIcon(data?.notificationVisualSettings?.icon ?? null);
    setIconBackgroundRadius(
      data?.notificationVisualSettings?.iconBackgroundRadius ??
        branding?.iconBackgroundRadius ??
        null,
    );
  }, [
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.textColor,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.roundness,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.icon,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    branding?.backgroundColor,
    branding?.textColor,
    branding?.iconBackgroundColor,
    branding?.borderRadius,
    branding?.backgroundOpacity,
    branding?.activeSize,
    branding?.brandingIsShowingIcon,
    branding?.iconBackgroundRadius,
  ]);

  useLayoutEffect(() => {
    const createNotificationPreviewHtml = async () => {
      const result = await window.getNotificationPreview(
        {
          campaign: {
            notificationVisualSettings: {
              roundness: 1,
            },
          },
          settings: {
            type: forOrders ? "lastOrders" : "lastRegistrations",
            notificationVisualSettings: {
              backgroundColor:
                backgroundColor ??
                data?.notificationVisualSettings?.backgroundColor ??
                branding?.backgroundColor ??
                "",
              textColor:
                textColor ??
                data?.notificationVisualSettings?.textColor ??
                branding?.textColor ??
                "",
              iconBackgroundColor:
                iconBackgroundColor ??
                data?.notificationVisualSettings?.iconBackgroundColor ??
                branding?.iconBackgroundColor ??
                "",
              iconColor:
                iconColor ??
                data?.notificationVisualSettings?.iconColor ??
                branding?.iconColor ??
                "",
              icon: icon ?? data?.notificationVisualSettings?.icon ?? "",
              roundness:
                borderRadius ??
                data?.notificationVisualSettings?.roundness ??
                branding?.borderRadius ??
                0,
              linkTextColor: "",
              linkButtonBackgroundColor: "",
              backgroundOpacity:
                backgroundOpacity ??
                data?.notificationVisualSettings?.backgroundOpacity ??
                branding?.backgroundOpacity ??
                1,
              activeSize:
                active ??
                data?.notificationVisualSettings?.activeSize ??
                branding?.activeSize ??
                "classic",
              isShowingIcon:
                showIcon ??
                data?.notificationVisualSettings?.isShowingIcon ??
                branding?.brandingIsShowingIcon ??
                true,
              iconBackgroundRadius:
                iconBackgroundRadius ??
                data?.notificationVisualSettings?.iconBackgroundRadius ??
                branding?.iconBackgroundRadius ??
                0,
              customWebBranding,
            },
          },
        },
        forOrders ? "lastOrders" : "lastRegistrations",
        t("campaign.notifications.lastOrders.previewText1") ?? "",
        text,
        undefined,
        language,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        notificationStyleId,
      );

      // Transforms object htmldivelement to pure string
      const cleanHtmlString = DOMPurify.sanitize(result, {
        USE_PROFILES: { html: true, svg: true },
      });
      setNotificationHtml(cleanHtmlString);
    };

    createNotificationPreviewHtml();
  }, [
    branding,
    iconBackgroundColor,
    textColor,
    backgroundColor,
    borderRadius,
    backgroundOpacity,
    iconBackgroundRadius,
    active,
    showIcon,
    forOrders,
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.textColor,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.iconColor,
    data?.notificationVisualSettings?.icon,
    data?.notificationVisualSettings?.roundness,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    iconColor,
    icon,
    t,
    text,
    language,
  ]);

  const onReset = () => {
    setActive(
      data?.notificationVisualSettings?.activeSize ??
        branding?.activeSize ??
        "classic",
    );
    setBackgroundColor(null);
    setTextColor(null);
    setBorderRadius(null);
    setBackgroundOpracity(null);
    setShowIcon(
      data?.notificationVisualSettings?.isShowingIcon !== null &&
        typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true,
    );
    setIconBackgroundColor(null);
    setIconBackgroundRadius(null);
    setIconColor(null);
    setIcon(null);
    setText(
      data?.text
        ? data.text
        : t(getOrdersAndRegistrationsInitialText(forOrders, language)) ?? "",
    );
    setUrlFilterForms(
      data?.urlFilter
        ? transformUrlFilterToForms(data.urlFilter)
        : [
            {
              control: "contains",
              include: ["/"],
              excludes: [],
            },
          ],
    );
    setMax(data ? getMax(data) : 10);
    setMin(data ? getMin(data) : 5);
    setDays(data?.maxLastDays !== undefined ? data?.maxLastDays : 5);
    setFallbackToGenericCustomer(
      data?.fallbackToGenericCustomer !== undefined
        ? data?.fallbackToGenericCustomer
        : true,
    );
    setShowCustomerOwnRegistrations(
      data?.showCustomerOwnRegistrations !== undefined
        ? data?.showCustomerOwnRegistrations
        : true,
    );
    setShowCustomerOwnOrders(
      data?.showCustomerOwnOrders !== undefined
        ? data.showCustomerOwnOrders
        : true,
    );
    setWithRepeat(
      data?.repeatNotification !== undefined ? data?.repeatNotification : true,
    );
  };

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue as TabType);
  };

  const isFormsFilled = () => {
    for (let i = 0; i < urlFilterForms.length; i++) {
      const form = urlFilterForms[i];
      if (form.excludes.length > 0 || form.include.length > 0) {
        return true;
      }
    }
    return false;
  };

  const onSave = async () => {
    if (text.length < 1) {
      setSelectedTab("rules");
      showFlashMessageWithTimeout(
        dispatch,
        t("popups.textMustBeFilled") ?? "",
        "error",
      );
      return;
    }
    if (!isFormsFilled()) {
      showFlashMessageWithTimeout(
        dispatch,
        t("popups.connectionMissing") ?? "",
        "error",
      );
      return;
    }
    try {
      const _notificationManager = NotificationManager.getManager();
      let body = {};
      if (!forOrders) {
        body = {
          id: createdNotificationId,
          isActive: true,
          repeatNotification: withRepeat,
          text: text,
          maxLastDays: days,
          minRegistrations: min,
          maxLastRegistrations: max,
          fallbackToGenericCustomer: fallbackToGenericCustomer,
          urlFilter: transformFormsToUrlFilterDto(urlFilterForms),
          showCustomerOwnRegistrations: showCustomerOwnRegistrations,

          notificationVisualSettings: {
            backgroundColor: backgroundColor ?? branding?.backgroundColor ?? "",
            textColor: textColor ?? branding?.textColor ?? "",
            iconBackgroundColor:
              iconBackgroundColor ?? branding?.iconBackgroundColor ?? "",
            iconColor: iconColor ?? branding?.iconColor ?? "",
            icon: icon ?? data?.notificationVisualSettings?.icon ?? "",
            roundness: borderRadius ?? branding?.borderRadius ?? 0,
            linkTextColor: "",
            linkButtonBackgroundColor: "",
            backgroundOpacity:
              backgroundOpacity ?? branding?.backgroundOpacity ?? 1,
            activeSize: active ?? branding?.activeSize ?? "classic",
            isShowingIcon: showIcon ?? branding?.brandingIsShowingIcon ?? true,
            iconBackgroundRadius:
              iconBackgroundRadius ?? branding?.iconBackgroundRadius ?? 0,
          },
        };
      } else {
        body = {
          id: createdNotificationId,
          isActive: true,
          repeatNotification: withRepeat,
          text: text,
          maxLastDays: days,
          minOrders: min,
          maxLastOrders: max,
          fallbackToGenericCustomer: fallbackToGenericCustomer,
          urlFilter: transformFormsToUrlFilterDto(urlFilterForms),
          showCustomerOwnOrders: showCustomerOwnOrders,

          notificationVisualSettings: {
            backgroundColor: backgroundColor ?? branding?.backgroundColor ?? "",
            textColor: textColor ?? branding?.textColor ?? "",
            iconBackgroundColor:
              iconBackgroundColor ?? branding?.iconBackgroundColor ?? "",
            iconColor: iconColor ?? branding?.iconColor ?? "",
            icon: icon ?? data?.notificationVisualSettings?.icon ?? "",
            roundness: borderRadius ?? branding?.borderRadius ?? 0,
            linkTextColor: "",
            linkButtonBackgroundColor: "",
            backgroundOpacity:
              backgroundOpacity ?? branding?.backgroundOpacity ?? 1,
            activeSize: active ?? branding?.activeSize ?? "classic",
            isShowingIcon: showIcon ?? branding?.brandingIsShowingIcon ?? true,
            iconBackgroundRadius:
              iconBackgroundRadius ?? branding?.iconBackgroundRadius ?? 0,
          },
        };
      }

      await _notificationManager.updateNotificationSettingsAsync(
        body as UpdateNotificationSettingsRequest,
      );
      onSaveSuccess?.();
      clearForms();
      onClose();
    } catch (e: any) {
      showFlashMessageWithTimeout(dispatch, e.toString(), "error");
    }
  };

  const clearForms = () => {
    setUrlFilterForms([{ control: "contains", include: ["/"], excludes: [] }]);
    setSelectedTab("rules");
    setMin(0);
    setMax(0);
    setText("");
    setDays(0);
    setFallbackToGenericCustomer(true);
    setWithRepeat(true);
  };

  const tabs = [
    {
      value: "settings",
      label: t("campaign.notifications.settings.customization.title"),
      content: (
        <NotificationSettingsTab
          notificationType='orderAndRegistrations'
          text={text}
          setText={setText}
        />
      ),
    },
    {
      value: "rules",
      label: t("campaign.notifications.settings.displayRules.title"),
      content: (
        <NotificationRulesTab
          rulesType={forOrders ? "lastOrders" : "lastRegistrations"}
          forOrders={forOrders}
          max={max}
          min={min}
          days={days}
          withRepeat={withRepeat}
          onChangeMax={val => setMax(val)}
          onChangeMin={val => setMin(val)}
          onChangeDays={val => setDays(val)}
          onToggleWithRepeat={() => setWithRepeat(!withRepeat)}
          t={t}
        />
      ),
    },
    {
      value: "pairing",
      label: t("campaign.notifications.settings.pairing.title"),
      content: (
        <NotificationPairingTab
          addEmptyPairingForm={addEmptyPairingForm}
          setForms={setUrlFilterForms}
          forms={urlFilterForms}
          forOrders={forOrders}
          handlePairingFormControlChange={handlePairingFormControlChange}
          onAddPairingFormExcludeChip={onAddPairingFormExcludeChip}
          onAddPairingFormIncludeChip={onAddPairingFormIncludeChip}
          onDeletePairingForm={onDeletePairingForm}
        />
      ),
    },
    {
      value: "design",
      label: t("campaign.notifications.settings.design.title"),
      content: (
        <NotificationDesignTab
          active={active}
          backgroundColor={backgroundColor}
          backgroundOpacity={backgroundOpacity}
          borderRadius={borderRadius}
          branding={branding}
          iconBackgroundColor={iconBackgroundColor}
          iconBackgroundRadius={iconBackgroundRadius}
          setActive={setActive}
          defaultIcon={
            forOrders
              ? NotificationDefaultSettings.OrdersIcon
              : NotificationDefaultSettings.RegisterIcon
          }
          setBackgroundColor={setBackgroundColor}
          setBackgroundOpracity={setBackgroundOpracity}
          setBorderRadius={setBorderRadius}
          setIconBackgroundColor={setIconBackgroundColor}
          setIconBackgroundRadius={setIconBackgroundRadius}
          setShowIcon={setShowIcon}
          setTextColor={setTextColor}
          showIcon={showIcon}
          textColor={textColor}
          disableIcon={false}
          icon={icon}
          iconColor={iconColor}
          setIcon={setIcon}
          setIconColor={setIconColor}
          data={data}
        />
      ),
    },
  ];

  return (
    <NotificationModalBase
      detectedChange={detectedChange}
      isOpen={isOpen}
      isNewNotification={!data}
      notificationHtml={notificationHtml}
      onClose={onClose}
      onReset={onReset}
      onSave={onSave}
      previewModalOpen={previewModalOpen}
      setPreviewModalOpen={setPreviewModalOpen}
      title={
        forOrders
          ? t("campaign.notifications.lastOrders.title")
          : t("campaign.notifications.lastRegistrations.title")
      }
    >
      <Tabs onTabChange={handleChange} selectedTab={selectedTab} tabs={tabs} />
    </NotificationModalBase>
  );
};

export default OrdersAndRegistrationsModal;
